import { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { IconLoader2, IconPlus, IconTrash, IconLanguage, IconCircleCheck, IconLayoutGrid, IconBuildingFactory2, IconPencil, IconStack2, IconCameraPlus } from '@tabler/icons-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useSectorPage } from '@/services/use-sector-page'
import { MediaPicker } from '@/components/media/media-picker'
import { IconPhoto } from '@tabler/icons-react'
import { getMediaUrl } from '@/utils/url-utils'

const localizedStringSchema = z.object({
    tr: z.string().min(1, 'Türkçe alan zorunludur'),
    en: z.string().optional(),
    fr: z.string().optional(),
    es: z.string().optional(),
    ru: z.string().optional(),
    nl: z.string().optional(),
    ar: z.string().optional(),
})

const sectorSchema = z.object({
    hero: z.object({
        title: localizedStringSchema,
        subtitle: localizedStringSchema,
    }),
    sectors: z.array(z.object({
        name: localizedStringSchema,
        description: localizedStringSchema,
        icon: z.string().optional(),
        imageUrl: z.string().optional(),
        orderIndex: z.number().optional().default(0),
    })),
})

type SectorFormValues = z.infer<typeof sectorSchema>

const topNav = [
    { title: 'Anasayfa', href: '/', isActive: false },
    { title: 'Sektörler CMS', href: '/sektorler', isActive: true },
]

const LANGUAGES = [
    { code: 'tr', label: 'Türkçe' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'ru', label: 'Русский' },
    { code: 'nl', label: 'Nederlands' },
    { code: 'ar', label: 'العربية' },
]

export default function Sektorler() {
    const { sectorPageQuery, updateSectorPageMutation } = useSectorPage()
    const sectorPage = sectorPageQuery.data
    const isLoading = sectorPageQuery.isLoading
    const isUpdating = updateSectorPageMutation.isPending

    const [isNormalSaving, setIsNormalSaving] = useState(false)
    const [isAISaving, setIsAISaving] = useState(false)
    const [showProgress, setShowProgress] = useState(false)
    const [progress, setProgress] = useState(0)
    const [currentStep, setCurrentStep] = useState('')
    const [sectorSearchQuery, setSectorSearchQuery] = useState('')
    const [isSectorDialogOpen, setIsSectorDialogOpen] = useState(false)
    const [editingSectorIndex, setEditingSectorIndex] = useState<number | null>(null)

    const form = useForm<SectorFormValues>({
        resolver: zodResolver(sectorSchema),
        defaultValues: {
            hero: { title: { tr: '' }, subtitle: { tr: '' } },
            sectors: [],
        },
    })

    const { fields: sectorFields, append: appendSector, remove: removeSector } = useFieldArray({
        control: form.control,
        name: 'sectors',
    })

    const [isPickerOpen, setIsPickerOpen] = useState(false)

    useEffect(() => {
        if (sectorPage) {
            form.reset({
                hero: sectorPage.hero,
                sectors: sectorPage.sectors || [],
            })
        }
    }, [sectorPage, form])

    const onSubmit = async (values: SectorFormValues) => {
        setIsNormalSaving(true)
        try {
            await updateSectorPageMutation.mutateAsync({ data: values })
        } finally {
            setIsNormalSaving(false)
        }
    }

    const onTranslateAndSave = async () => {
        const values = form.getValues()
        setIsAISaving(true)
        setShowProgress(true)
        setProgress(5)
        setCurrentStep('AI Çeviri servisine bağlanılıyor...')

        const steps = [
            { p: 25, m: 'Giriş bölümü çeviriliyor...' },
            { p: 60, m: 'Sektör başlıkları ve açıklamaları işleniyor...' },
            { p: 90, m: 'Veriler derleniyor...' },
            { p: 98, m: 'Sistem güncelleniyor...' },
        ]

        let currentStepIdx = 0
        const interval = setInterval(() => {
            if (currentStepIdx < steps.length) {
                setProgress(steps[currentStepIdx].p)
                setCurrentStep(steps[currentStepIdx].m)
                currentStepIdx++
            }
        }, 1500)

        try {
            await updateSectorPageMutation.mutateAsync({ data: values, translate: true })
            clearInterval(interval)
            setProgress(100)
            setCurrentStep('Tüm diller başarıyla kaydedildi!')
            setTimeout(() => setShowProgress(false), 2000)
        } catch (error) {
            clearInterval(interval)
            setShowProgress(false)
        } finally {
            setIsAISaving(false)
        }
    }

    if (isLoading) {
        return (
            <>
                <Header>
                    <TopNav links={topNav} />
                    <div className="ml-auto flex items-center gap-2">
                        <SearchComponent />
                        <ThemeSwitch />
                        <ProfileDropdown />
                    </div>
                </Header>
                <Main>
                    <div className="flex h-[80vh] items-center justify-center">
                        <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                </Main>
            </>
        )
    }

    return (
        <>
            <Header>
                <TopNav links={topNav} />
                <div className="ml-auto flex items-center gap-2">
                    <SearchComponent />
                    <ThemeSwitch />
                    <ProfileDropdown />
                </div>
            </Header>

            <Main>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                        <div className='flex flex-wrap items-center justify-between space-y-2'>
                            <div>
                                <h2 className='text-2xl font-bold tracking-tight'>Sektör Yönetimi</h2>
                                <p className='text-muted-foreground'>
                                    Hizmet verdiğiniz sektörleri ve çözümleri yönetin.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onTranslateAndSave}
                                    disabled={isUpdating}
                                    className="space-x-1 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                                >
                                    <span>AI ile Çevir ve Kaydet</span>
                                    {isAISaving ? <IconLoader2 size={18} className="animate-spin" /> : <IconLanguage size={18} />}
                                </Button>
                                <Button type="submit" disabled={isUpdating} className="space-x-1">
                                    <span>Normal Kaydet</span>
                                    {isNormalSaving ? <IconLoader2 size={18} className="animate-spin" /> : <IconCircleCheck size={18} />}
                                </Button>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">Toplam Sektör</CardTitle>
                                    <IconLayoutGrid className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-primary">{sectorFields.length}</div>
                                    <p className="text-xs text-muted-foreground">Aktif faaliyet gösterilen alanlar</p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">Sayfa Bölümleri</CardTitle>
                                    <IconStack2 className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold text-primary">2</div>
                                    <p className="text-xs text-muted-foreground">Hero ve Sektör Listesi bölümleri</p>
                                </CardContent>
                            </Card>
                        </div>

                        <Tabs defaultValue="hero" className="w-full">
                            <TabsList className="grid w-full grid-cols-2 h-auto">
                                <TabsTrigger value="hero" className="py-2">Giriş (Hero)</TabsTrigger>
                                <TabsTrigger value="sectors" className="py-2">Sektörler</TabsTrigger>
                            </TabsList>

                            {/* HERO SECTION */}
                            <TabsContent value="hero" className="space-y-4 pt-4">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Sayfa Başlığı ve Alt Başlık</CardTitle>
                                        <CardDescription>Sektörler sayfasının giriş bölümündeki metinler.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <Tabs defaultValue="tr" className="w-full">
                                            <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                                                {LANGUAGES.map(lang => (
                                                    <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                                                ))}
                                            </TabsList>
                                            {LANGUAGES.map(lang => (
                                                <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                                                    <FormField
                                                        control={form.control}
                                                        name={`hero.title.${lang.code}` as any}
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>Başlık ({lang.label})</FormLabel>
                                                                <FormControl>
                                                                    <Input {...field} placeholder="Örn: Sektörel Çözümlerimiz" />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                    <FormField
                                                        control={form.control}
                                                        name={`hero.subtitle.${lang.code}` as any}
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>Alt Başlık ({lang.label})</FormLabel>
                                                                <FormControl>
                                                                    <Input {...field} placeholder="Örn: Hizmet Verilen Alanlar" />
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </TabsContent>
                                            ))}
                                        </Tabs>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* SECTORS SECTION */}
                            <TabsContent value="sectors" className="space-y-6 pt-4">
                                <div className='flex items-center justify-between py-1'>
                                    <div className='flex flex-1 flex-col-reverse items-start gap-y-2 sm:flex-row sm:items-center sm:space-x-2'>
                                        <Input
                                            placeholder='Sektör İsmi Ara...'
                                            value={sectorSearchQuery}
                                            onChange={(e) => setSectorSearchQuery(e.target.value)}
                                            className='h-8 w-[150px] lg:w-[250px]'
                                        />
                                    </div>
                                    <div className='flex gap-2'>
                                        <Button
                                            type='button'
                                            variant='outline'
                                            className='space-x-1 h-8 px-2 lg:px-3 text-xs'
                                            onClick={() => {
                                                appendSector({
                                                    name: { tr: '' },
                                                    description: { tr: '' },
                                                    icon: '',
                                                    imageUrl: '',
                                                    orderIndex: sectorFields.length
                                                })
                                                setEditingSectorIndex(sectorFields.length)
                                                setIsSectorDialogOpen(true)
                                            }}
                                        >
                                            <span>Yeni Sektör</span> <IconPlus size={16} />
                                        </Button>
                                    </div>
                                </div>

                                <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
                                    <div className='rounded-md border'>
                                        <Table>
                                            <TableHeader>
                                                <TableRow className='group/row'>
                                                    <TableHead className="w-[80px]">Görsel</TableHead>
                                                    <TableHead>Sektör Adı</TableHead>
                                                    <TableHead>Açıklama</TableHead>
                                                    <TableHead className="w-[100px]">İkon</TableHead>
                                                    <TableHead className="text-right">İşlemler</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {sectorFields.map((field, index) => {
                                                    const name = form.watch(`sectors.${index}.name.tr`) || ''
                                                    const description = form.watch(`sectors.${index}.description.tr`) || ''
                                                    const imageUrl = form.watch(`sectors.${index}.imageUrl`) || ''
                                                    const icon = form.watch(`sectors.${index}.icon`) || ''

                                                    const query = sectorSearchQuery.toLowerCase()
                                                    if (query && !name.toLowerCase().includes(query)) {
                                                        return null
                                                    }

                                                    return (
                                                        <TableRow key={field.id} className="group hover:bg-muted/40 transition-colors">
                                                            <TableCell>
                                                                <Avatar className="h-12 w-16 rounded-md ring-1 ring-muted shadow-sm">
                                                                    <AvatarImage src={getMediaUrl(imageUrl)} className="object-cover" />
                                                                    <AvatarFallback className="bg-primary/5 text-primary text-[10px] font-bold rounded-md">
                                                                        IMG
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                            </TableCell>
                                                            <TableCell>
                                                                <span className="font-semibold text-sm text-foreground">{name || 'İsimsiz Sektör'}</span>
                                                            </TableCell>
                                                            <TableCell>
                                                                <p className="text-[11px] text-muted-foreground line-clamp-1 max-w-[300px]">
                                                                    {description || 'Açıklama girilmemiş.'}
                                                                </p>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge variant="secondary" className="font-mono text-[10px]">
                                                                    {icon || 'Belirtilmedi'}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="text-right py-2">
                                                                <div className="flex justify-end gap-1">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                                        onClick={() => {
                                                                            setEditingSectorIndex(index)
                                                                            setIsSectorDialogOpen(true)
                                                                        }}
                                                                    >
                                                                        <IconPencil size={14} />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                                                        onClick={() => removeSector(index)}
                                                                    >
                                                                        <IconTrash size={14} />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                                {sectorFields.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="h-48 text-center bg-muted/5">
                                                            <div className="flex flex-col items-center justify-center gap-2">
                                                                <IconBuildingFactory2 className="h-8 w-8 text-muted-foreground/30" />
                                                                <p className="text-sm text-muted-foreground font-medium italic">Henüz sektör tanımlanmamış.</p>
                                                                <Button type="button" variant="outline" size="sm" className="mt-2 space-x-1" onClick={() => {
                                                                    appendSector({
                                                                        name: { tr: '' },
                                                                        description: { tr: '' },
                                                                        icon: '',
                                                                        imageUrl: '',
                                                                        orderIndex: 0
                                                                    })
                                                                    setEditingSectorIndex(sectorFields.length)
                                                                    setIsSectorDialogOpen(true)
                                                                }}>
                                                                    <span>İlk Sektörü Ekle</span> <IconPlus size={16} />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>

                                {/* SECTOR DIALOG */}
                                <Dialog open={isSectorDialogOpen} onOpenChange={setIsSectorDialogOpen}>
                                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                                        <DialogHeader>
                                            <DialogTitle className="flex items-center gap-2">
                                                {editingSectorIndex !== null ? <IconPencil className="h-5 w-5" /> : <IconPlus className="h-5 w-5" />}
                                                {editingSectorIndex !== null ? 'Sektör Bilgilerini Güncelle' : 'Yeni Sektör Ekle'}
                                            </DialogTitle>
                                            <DialogDescription>
                                                Sektörün tüm dillerdeki bilgilerini, görselini ve ikonunu buradan yönetebilirsiniz.
                                            </DialogDescription>
                                        </DialogHeader>

                                        <div className="space-y-6 py-4">
                                            {editingSectorIndex !== null && (() => {
                                                const index = editingSectorIndex;
                                                return (
                                                    <div className="space-y-6">
                                                        <div className="flex flex-col md:flex-row gap-6">
                                                            <div className="flex flex-col items-center gap-4">
                                                                <div className="relative group/image cursor-pointer w-48 h-64 rounded-xl overflow-hidden shadow-lg border-2 border-muted transition-all hover:border-primary/50" onClick={() => setIsPickerOpen(true)}>
                                                                    <Avatar className="w-full h-full rounded-none">
                                                                        <AvatarImage src={getMediaUrl(form.watch(`sectors.${index}.imageUrl`))} className="object-cover" />
                                                                        <AvatarFallback className="bg-primary/5 rounded-none flex items-center justify-center">
                                                                            <IconCameraPlus className="h-10 w-10 text-muted-foreground" />
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/image:opacity-100 transition-opacity">
                                                                        <span className="text-white text-xs font-bold">Görsel Seç</span>
                                                                    </div>
                                                                </div>
                                                                <Button type="button" variant="outline" size="sm" onClick={() => setIsPickerOpen(true)}>
                                                                    <IconPhoto size={16} className="mr-2" /> Görsel Seç
                                                                </Button>
                                                                <MediaPicker
                                                                    open={isPickerOpen}
                                                                    onOpenChange={setIsPickerOpen}
                                                                    onSelect={(url) => {
                                                                        form.setValue(`sectors.${index}.imageUrl`, url, { shouldDirty: true })
                                                                        setIsPickerOpen(false)
                                                                    }}
                                                                    title="Sektör Görseli Seç"
                                                                />
                                                            </div>

                                                            <div className="flex-1 space-y-4">
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`sectors.${index}.icon`}
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel className="flex items-center gap-2">
                                                                                <IconLayoutGrid size={14} /> Lucide İkon Adı (building2, pickaxe, tractor vb.)
                                                                            </FormLabel>
                                                                            <FormControl>
                                                                                <Input {...field} placeholder="Örn: building2" />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`sectors.${index}.orderIndex`}
                                                                    render={({ field: { value, onChange, ...field } }) => (
                                                                        <FormItem>
                                                                            <FormLabel>Sıralama</FormLabel>
                                                                            <FormControl>
                                                                                <Input
                                                                                    type="number"
                                                                                    {...field}
                                                                                    value={value}
                                                                                    onChange={(e) => onChange(parseInt(e.target.value))}
                                                                                />
                                                                            </FormControl>
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>

                                                        <Tabs defaultValue="tr" className="w-full">
                                                            <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                                                                {LANGUAGES.map(lang => (
                                                                    <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-4 py-1.5">{lang.label}</TabsTrigger>
                                                                ))}
                                                            </TabsList>
                                                            {LANGUAGES.map(lang => (
                                                                <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4 animate-in fade-in-50 duration-300">
                                                                    <FormField
                                                                        control={form.control}
                                                                        name={`sectors.${index}.name.${lang.code}` as any}
                                                                        render={({ field }) => (
                                                                            <FormItem>
                                                                                <FormLabel className="font-semibold text-xs text-primary uppercase tracking-wider">Sektör Adı ({lang.code.toUpperCase()})</FormLabel>
                                                                                <FormControl>
                                                                                    <Input {...field} placeholder="Örn: İnşaat" />
                                                                                </FormControl>
                                                                                <FormMessage />
                                                                            </FormItem>
                                                                        )}
                                                                    />
                                                                    <FormField
                                                                        control={form.control}
                                                                        name={`sectors.${index}.description.${lang.code}` as any}
                                                                        render={({ field }) => (
                                                                            <FormItem>
                                                                                <FormLabel className="font-semibold text-xs text-primary uppercase tracking-wider">Açıklama ({lang.code.toUpperCase()})</FormLabel>
                                                                                <FormControl>
                                                                                    <Textarea {...field} placeholder="Sektörel çözüm detayı..." rows={3} />
                                                                                </FormControl>
                                                                                <FormMessage />
                                                                            </FormItem>
                                                                        )}
                                                                    />
                                                                </TabsContent>
                                                            ))}
                                                        </Tabs>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <div className="flex justify-end pt-4 border-t">
                                            <Button type="button" onClick={() => setIsSectorDialogOpen(false)}>
                                                Tamamlandı
                                            </Button>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </TabsContent>
                        </Tabs>
                    </form>
                </Form>
            </Main>

            <Dialog open={showProgress} onOpenChange={setShowProgress}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <IconLanguage className="h-5 w-5 text-blue-600" />
                            Yapay Zeka Çevirisi
                        </DialogTitle>
                        <DialogDescription>
                            İçeriğiniz 7 farklı dile profesyonelce çevriliyor. Lütfen bekleyin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm font-medium">
                                <span>{currentStep}</span>
                                <span>%{progress}</span>
                            </div>
                            <Progress value={progress} className="h-2 w-full" />
                        </div>
                        {progress === 100 && (
                            <div className="flex items-center justify-center gap-2 text-green-600 animate-in fade-in zoom-in duration-300">
                                <IconCircleCheck className="h-5 w-5" />
                                <span className="font-medium text-sm">Başarıyla tamamlandı ve tün diller kaydedildi!</span>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
